import { useState } from "react";
import { Trash2, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Owner / manager only. Wipes EVERY job in this workshop.
 * Requires typing "DELETE" exactly into the confirm field — designed so
 * Steve can clear a test diary import without nuking the workshop itself,
 * but advisors can't trigger it accidentally on the showroom floor.
 *
 * Props:
 *   onWiped — callback fired after a successful wipe (parent refreshes jobs list)
 *   className — optional class overrides for placement
 */
export default function WipeAllJobsButton({ onWiped, className = "" }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");
  // Boardroom demo accounts never see the nuke button — one accidental tap
  // during Steve's pitch would wipe the entire RRG demo mid-flow.
  if (user?.is_demo_user) return null;

  const doWipe = async () => {
    if (typed !== "DELETE") return;
    setBusy(true);
    try {
      const r = await api.post("/jobs/bulk-delete", { confirm: "DELETE" });
      const n = r.data?.counts?.jobs || 0;
      toast.success(`Wiped ${n} job${n === 1 ? "" : "s"} from this workshop`);
      setConfirming(false);
      setTyped("");
      if (onWiped) onWiped();
    } catch (e) {
      const detail = e?.response?.data?.detail
        || (e?.response?.status ? `Server returned ${e.response.status}` : null)
        || e?.message
        || "Couldn't wipe jobs";
      toast.error(`Wipe failed: ${detail}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setConfirming(true)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-destructive/40 bg-destructive/[0.06] hover:bg-destructive/[0.15] text-destructive text-sm font-bold transition-colors ${className}`}
        data-testid="wipe-all-jobs-btn"
        title="Owner only \u2014 deletes every job in this workshop"
      >
        <Trash2 className="w-4 h-4" /> <span className="hidden sm:inline">Wipe all jobs</span>
      </button>

      {confirming && (
        <div
          className="fixed inset-0 z-[80] grid place-items-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => !busy && setConfirming(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-card rounded-lg border border-destructive/50 shadow-xl max-w-md w-full p-6"
            data-testid="wipe-all-jobs-modal"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-destructive/15 text-destructive grid place-items-center shrink-0">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-display font-bold text-lg tracking-tight">Wipe every job in this workshop?</h2>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                  This deletes every job, its messages and QC flags for this workshop only.
                  Workshop settings, branding, templates and team members are untouched.
                </p>
                <p className="text-xs text-destructive mt-2 font-semibold">
                  This cannot be undone.
                </p>
                <label className="block mt-4">
                  <span className="text-xs text-muted-foreground">Type <code className="font-mono-plex font-bold text-foreground">DELETE</code> to confirm:</span>
                  <input
                    type="text"
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    autoFocus
                    disabled={busy}
                    className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background text-sm font-mono-plex"
                    data-testid="wipe-all-jobs-confirm-input"
                    placeholder="DELETE"
                  />
                </label>
                <div className="flex gap-2 mt-4">
                  <button
                    type="button"
                    onClick={doWipe}
                    disabled={busy || typed !== "DELETE"}
                    className="btn-primary flex-1 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="wipe-all-jobs-confirm-btn"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    Wipe all jobs
                  </button>
                  <button
                    type="button"
                    onClick={() => { setConfirming(false); setTyped(""); }}
                    disabled={busy}
                    className="btn-outline flex-1"
                    data-testid="wipe-all-jobs-cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
